import React, { FC, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { CompanyContactInfo, CompanyContactInfoEdit } from './CompanyContactInfo';
import { Color, layoutStyles, cardStyles, borderRadius, mediaQuery } from '../Theme';
import { Header } from '../Header';
import { CompanyAccordion } from './CompanyAccordion';
import { nextApiRequest } from '../../lib/utils';
import { WWWCompanyDocument } from '../../../common/documents/WWWCompanyDocument';
import { CompanyImage, CompanyImageEdit } from './CompanyImage';
import { CompanyBasicInfo, CompanyBasicInfoEdit } from './CompanyBasicInfo';
import { CompanyOperationInfo, CompanyOperationInfoEdit } from './CompanyOperationInfo';
import { CompanyTrustInfo } from './CompanyTrustInfo';
import { CompanyCertificateInfo } from './CompanyCertificateInfo';
import { CompanyFinanceInfo } from './CompanyFinanceInfo';
import { CompanyEditDialog } from './CompanyEditDialog';
import { useUser } from '../../context/useUser';
import { openPaywall } from '../../context/paywallStore';
import { darken, lighten } from 'polished';
import { useCompanyActionsInPage } from './useCompanyActions';
import { useCompanyActionMenuInPage } from './useCompanyActionMenu';
import { IsInListIndicator } from '../company-list/CompanyListRow';
import IconButton from '@material-ui/core/IconButton';
import ClipboardTextIcon from 'mdi-react/ClipboardTextIcon';
import ClipboardTextOutlineIcon from 'mdi-react/ClipboardTextOutlineIcon';
import { CompanyPage as SEO } from '../SEO';
import { AccountUserAccessRole } from '../../common/Constants';
import { OmnibarPremiumBanner } from '../omnibar/OmnibarPremiumBanner';
import { FormattedMessage } from 'react-intl';
import { recordCompanyVisit } from '../../lib/companyHistory';
import { Banner } from '../Banner';
import { CompanyReferenceEdit } from './CompanyReferenceEdit';
import { useCompanyDictionary } from './CompanyDictionary';
import { CompanyReferenceInfo } from './CompanyReferenceInfo';
import { CompanyPrivateFields } from './CompanyPrivateFields';

//
// Company page
// /[lang]/[hash]/[companyname]
//
export const Content = styled.div`
  ${cardStyles}
  margin: 0 auto 6rem auto;
  overflow: hidden;
`;

export const Hero = styled.div`
  ${layoutStyles}
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: 2rem;
  padding: 2rem 0;

  h2 {
    font-size: 2.8rem;
    margin-bottom: 0rem;
  }

  p {
    display: flex;
    align-items: center;
    margin-top: 0.3rem;
    margin-bottom: 0;
    font-weight: normal;
    font-size: 1rem;
    color: ${lighten(0.3, Color.black)};
  }

  a {
    color: ${Color.primary};
  }

  b {
    margin-left: 0.3rem;
    padding: 0.2rem 0.4rem;
    background-color: ${darken(0.1, Color.background)};
    border-radius: ${borderRadius}px;
    color: ${Color.black};
  }

  ${mediaQuery.small(css`
    flex-direction: column;

    h2 {
      font-size: 1.8rem;
    }
  `)}
`;

const HeroInfo = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  margin-left: 2rem;

  > div:first-child {
    flex: 1;
  }

  ${mediaQuery.small(css`
    flex-direction: column;
    align-items: center;
    margin-left: 0;

    > div {
      margin-top: 1.5rem;
      text-align: center;

      p {
        justify-content: center;
      }
    }
  `)}
`;

const Actions = styled.div`
  ${mediaQuery.small(css`
    margin-top: 0.5rem;
  `)}
`;

export const Scaffold = styled.div`
  ${layoutStyles}
  margin-bottom: 1rem;
  border-radius: ${borderRadius}px;
  overflow: hidden;
`;

export interface CompanyPageProps {
  company: WWWCompanyDocument;
}

export const CompanyPage: FC<CompanyPageProps> = ({ company: ssrCompany, ...other }) => {
  const user = useUser();
  const dictionary = useCompanyDictionary();

  const [dynamicCompany, setDynamicCompany] = useState<WWWCompanyDocument | null>(null);
  const [trustInfo, setTrustInfo] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [editDialogSection, setEditDialogSection] = useState<string | null>(null);

  const [openSections, setOpenSections] = useState<string[]>([
    'trustable',
    'operations',
    'certifications',
    'references',
    'contacts',
    'finance',
  ]);

  // Combine the original company data with the dynamically loaded one
  const company = { ...ssrCompany, ...(dynamicCompany || {}) };

  const { isInCustomList, renderThumbUpButton, renderThumbDownButton } = useCompanyActionsInPage(company);
  const { handleMenuOpen, renderMenu } = useCompanyActionMenuInPage(company);

  const userCompany = user.companies[0] || null;
  const premium = user.hasPremium || userCompany?.businessID == company.businessID || dynamicCompany?.isPremium || false; // Can see own company
  const userOwnCompany = userCompany?.businessID === company.businessID;
  const editable =
    userOwnCompany &&
    user.accessRoleID === AccountUserAccessRole.ADMIN &&
    (userCompany.isCompanyManualFormPending || !userCompany.isPending);
  const isPremiumOverride = !!dynamicCompany?.premiumOverrideReason;

  const editSections = {
    image: CompanyImageEdit,
    basic: CompanyBasicInfoEdit,
    references: CompanyReferenceEdit,
    operations: CompanyOperationInfoEdit,
    contacts: CompanyContactInfoEdit,
  };

  // Load dynamic data
  useEffect(() => {
    if (!ssrCompany) return;

    (async () => {
      const result = await nextApiRequest('/api/v1/www/company/load', { companyID: ssrCompany.companyID }, { delay: 2000 });

      if (result.status == 'ok') {
        setDynamicCompany(result.company);
      } else {
        console.log('Error loading company', result);
        // TODO: Show error banner
      }

      // only client side
      if (typeof window !== 'undefined') {
        // TODO: Should this be a part of the load request??
        const { status, tilaajavastuu } = await nextApiRequest('/api/v1/company/tilaajavastuu', { businessID: company.businessID });
        if (status !== 'ok' || !tilaajavastuu) {
          console.error('Failed to load tilaajavastuu', status, tilaajavastuu);
        } else {
          setTrustInfo(tilaajavastuu);
        }
      }
    })();
  }, [ssrCompany?.companyID]);

  // Show loading state if expecting premium data
  useEffect(() => {
    const isExpectingMoreData = premium || editable;
    if (!isExpectingMoreData) return setLoading(false);

    setLoading(!dynamicCompany);
  }, [dynamicCompany, premium || editable]);

  // Update company history
  useEffect(() => {
    recordCompanyVisit(company);
  }, [company.companyID]);

  // Show paywall banner if scrolled too far
  useEffect(() => {
    if (premium) return;
    if (loading) return;

    // Show paywall after 10 secinds, even if not scrolling
    const timeout = setTimeout(() => openPaywall('company'), 10000);

    const scrollListener = () => {
      if (window.scrollY > 800) openPaywall('company');
    };

    window.addEventListener('scroll', scrollListener, { passive: true });
    return () => {
      window.removeEventListener('scroll', scrollListener);
      clearTimeout(timeout);
    };
  }, [loading, premium, ssrCompany?.companyID]);

  /** This is called from the company edit dialog when edits are saved */
  const handleDocumentUpdate = (document: WWWCompanyDocument) => {
    setDynamicCompany(document);
  };

  const handleSectionChange = (id: string) => (event: any, expanded: boolean) => {
    if (expanded) {
      setOpenSections([...openSections, id]);
    } else {
      setOpenSections(openSections.filter((section) => section !== id));
    }
  };

  const handleEditDialogOpen = (section: string) => () => {
    setEditDialogSection(section);
  };

  const handleEditDialogClose = () => {
    setEditDialogSection(null);
  };

  const sectionProps = { company, editable, premium, loading: loading || user.isSSRRender };

  const premiumPreviewDescription =
    dynamicCompany?.premiumOverrideReason === 'recently-updated' ? (
      <FormattedMessage
        id="company.premiumPreview.recentlyUpdatedDescription"
        defaultMessage={
          'Koska tämä yritys on nostettu etusivun "Viimeksi päivitetyt" listaukseen, näet kaikki sen tiedot ilmaiseksi. Jos haluat nähdä yritysten kaikki tiedot myös tulevaisuudessa, tilaa Constle -premium jäsenyys.'
        }
      />
    ) : (
      <FormattedMessage
        id="company.premiumPreview.description"
        defaultMessage={
          'Tämä yritys on "demo" tilassa, joten näet kaikki sen tiedot ilmaiseksi. Jos haluat nähdä yritysten kaikki tiedot myös tulevaisuudessa, tilaa Constle -premium jäsenyys.'
        }
      />
    );

  return (
    <>
      <SEO company={company} />

      <Hero>
        <CompanyImage {...sectionProps} onEdit={handleEditDialogOpen('image')} />
        <HeroInfo>
          <div>
            <Header>{company.name}</Header>
            <p>
              Y-tunnus: <b>{company.businessID}</b>
            </p>
          </div>
          <Actions>
            {renderThumbUpButton()}
            {renderThumbDownButton()}
            <IconButton aria-label="more options" component="span" disabled={!user.isLoggedIn} onClick={handleMenuOpen}>
              {isInCustomList ? <ClipboardTextIcon /> : <ClipboardTextOutlineIcon />}
              {isInCustomList && <IsInListIndicator />}
            </IconButton>
          </Actions>
        </HeroInfo>
      </Hero>

      {dynamicCompany?.isPremium && !user.hasPremium && !userOwnCompany && (
        <Scaffold>
          <OmnibarPremiumBanner
            title={<FormattedMessage id="company.premiumPreview.title" defaultMessage="Constle Pro -esikatselu" />}
            description={premiumPreviewDescription}
          />
        </Scaffold>
      )}

      {userCompany?.isUserManualFormPending && !userCompany.isCompanyManualFormPending && (
        <Hero>
          <Banner type="warning" style={{ width: '100%' }}>
            <FormattedMessage
              id="company.companyUserPendingEdit.title"
              defaultMessage="HUOM! Rekisteröitymisesi on käsittelyssä. Voit muokata yrityksen tietoja vasta, kun Constle on hyväksynyt oikeutesi muokata yritystä."
            />
          </Banner>
        </Hero>
      )}

      {userOwnCompany && editable && userCompany.isCompanyManualFormPending && (
        <Hero>
          <Banner type="warning" style={{ width: '100%' }}>
            <FormattedMessage
              id="company.companyPendingEdit.title"
              defaultMessage="HUOM! Yrityksesi liittämispyyntö on käsittelyssä. Voit muokata yrityksen tietoja, ja päivitämme yrityssivun Constlen hakuun hyväksymisen jälkeen."
            />
          </Banner>
        </Hero>
      )}

      <Content>
        <CompanyBasicInfo {...sectionProps} editable={editable} onEdit={handleEditDialogOpen('basic')} />

        <CompanyPrivateFields company={company} />
        <CompanyAccordion
          title="Luotettava kumppani"
          content={<CompanyTrustInfo {...sectionProps} trustInfo={trustInfo} isPremiumOverride={isPremiumOverride} />}
          onChange={handleSectionChange('trustable')}
          expanded={openSections.includes('trustable')}
        />
        <CompanyAccordion
          title="Toiminta"
          content={<CompanyOperationInfo {...sectionProps} />}
          onChange={handleSectionChange('operations')}
          expanded={openSections.includes('operations')}
          editable={editable}
          onEdit={handleEditDialogOpen('operations')}
        />
        {(editable || company.references.length > 0) && (
          <CompanyAccordion
            title={dictionary.references}
            content={<CompanyReferenceInfo {...sectionProps} />}
            onChange={handleSectionChange('references')}
            expanded={openSections.includes('references')}
            editable={editable}
            onEdit={handleEditDialogOpen('references')}
          />
        )}
        {company.taitoCompetences.length > 0 && (
          <CompanyAccordion
            title="Sertifikaatit"
            content={<CompanyCertificateInfo {...sectionProps} />}
            onChange={handleSectionChange('certifications')}
            expanded={openSections.includes('certifications')}
          />
        )}
        {company.financials.length > 0 && (
          <CompanyAccordion
            title="Taloustiedot"
            content={<CompanyFinanceInfo {...sectionProps} />}
            onChange={handleSectionChange('finance')}
            expanded={openSections.includes('finance')}
          />
        )}
        {(editable || company.locations.length > 0 || company.contactPersons.length > 0 || !premium) && (
          <CompanyAccordion
            title="Yhteystiedot"
            content={<CompanyContactInfo {...sectionProps} />}
            onChange={handleSectionChange('contacts')}
            expanded={openSections.includes('contacts')}
            editable={editable}
            onEdit={handleEditDialogOpen('contacts')}
          />
        )}
      </Content>

      <CompanyEditDialog
        open={!!editDialogSection}
        company={company}
        content={editSections[editDialogSection]}
        onUpdate={handleDocumentUpdate}
        onClose={handleEditDialogClose}
      />

      {renderMenu()}
    </>
  );
};
